import { createTheme } from '@mui/material/styles';
import palette from './palette';
import tokens from './tokens';

const theme = createTheme({
  palette: {
    common: {
      white: palette.white,
    },
    primary: {
      main: palette.primary[50],
      dark: tokens.colors.primaryDeep,
      light: palette.primary[30],
      100: palette.primary[10],
      200: palette.primary[20],
      400: palette.primary[40],
      800: palette.primary[80],
    },
    secondary: {
      main: palette.secondary[10],
      100: palette.secondary[10],
    },
    success: {
      main: palette.success[60],
      600: palette.success[60],
    },
    error: {
      main: palette.error[60],
      600: palette.error[60],
    },
    warning: {
      main: tokens.colors.warning.main,
    },
    info: {
      main: tokens.colors.info.main,
    },
    grey: {
      300: palette.grey[30],
      400: palette.grey[40],
      500: palette.grey[50],
      600: palette.grey[60],
      700: palette.grey[70],
      800: palette.grey[80],
      900: palette.grey[90],
    },
    text: {
      primary: palette.text.primary,
      secondary: palette.text.secondary,
    },
    background: {
      default: tokens.colors.pageBg,
      paper: tokens.colors.surface,
    },
    divider: tokens.colors.border,
  },
  shape: {
    borderRadius: tokens.radius.sm,
  },
});

const displayFont = tokens.fonts.display;
const bodyFont = tokens.fonts.body;

const customTheme = createTheme(theme, {
  typography: {
    fontFamily: bodyFont,
    allVariants: {
      color: palette.text.primary,
    },
    h1: {
      fontFamily: displayFont,
      fontSize: 32,
      lineHeight: 1.15,
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: displayFont,
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 32 / 24,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: displayFont,
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 28 / 20,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: displayFont,
      letterSpacing: 0,
      fontSize: 17,
      fontWeight: 600,
      lineHeight: 24 / 17,
    },
    h5: {
      fontFamily: bodyFont,
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 22 / 15,
    },
    subtitle1: {
      fontFamily: bodyFont,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body1: {
      fontFamily: bodyFont,
      fontSize: 14,
      lineHeight: 20 / 14,
      letterSpacing: 0,
    },
    subtitle2: {
      fontFamily: bodyFont,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.43,
    },
    body2: {
      fontFamily: bodyFont,
      fontSize: 14,
      lineHeight: 1.43,
      letterSpacing: 0,
    },
    overline: {
      fontFamily: bodyFont,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: palette.grey[50],
    },
    caption: {
      fontFamily: bodyFont,
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: '0.02em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.colors.pageBg,
          color: tokens.colors.ink,
        },
        '*:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 3px ${tokens.colors.focusRing}`,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.MuiButton-sizeMedium': {
            borderRadius: tokens.radius.sm,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '0.563rem 1rem',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: tokens.radius.sm,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: tokens.colors.primaryDeep,
          },
        },
        outlined: {
          borderColor: tokens.colors.borderStrong,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.pill,
          fontWeight: 600,
          '&.MuiChip-sizeMedium': {
            height: '22px',
            fontSize: '12px',
          },
          '&.MuiChip-sizeSmall': {
            height: '18px',
            fontSize: '10px',
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: tokens.colors.success.bg,
            color: tokens.colors.success.fg,
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: tokens.colors.info.bg,
            color: tokens.colors.info.fg,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: tokens.colors.warning.bg,
            color: tokens.colors.warning.fg,
          },
          '&.MuiChip-colorError': {
            backgroundColor: tokens.colors.error.bg,
            color: tokens.colors.error.fg,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: tokens.radius.md,
        },
        elevation1: {
          boxShadow: tokens.shadows.card,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.shadows.overlay,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.borderStrong,
            borderRadius: tokens.radius.sm,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.borderStrong,
            borderRadius: tokens.radius.sm,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
        indicator: {
          backgroundColor: tokens.colors.primary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 14,
          minHeight: 40,
          color: tokens.colors.inkSecondary,
          '&.Mui-selected': {
            color: tokens.colors.primaryDeep,
            fontWeight: 600,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.colors.primary,
          textDecorationColor: tokens.colors.primaryTint,
          '&:hover': {
            color: tokens.colors.primaryDeep,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: tokens.colors.border,
        },
        head: {
          backgroundColor: tokens.colors.tableHeadBg,
          color: tokens.colors.inkSecondary,
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          lineHeight: 1.4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.colors.ink,
          fontSize: 12,
          borderRadius: tokens.radius.sm,
        },
      },
    },
  },
});

export default customTheme;
